// ==========================
// Version 5 — pages/Profile.js (LIVE memories + minted indicator)
// - v4 + uses onSnapshot for user's memories (so NFT mint status updates live)
// - Shows ✅ Minted label in "Your Memories" list when nft.latest exists
// - No UX/layout changes beyond that
// ==========================
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isMemoryMarkedMinted } from '../nft/status';
import { saveUserPrefs, watchUserPrefs, setUserProfile } from '../db';
import Seo from '../components/Seo';
import { auth, db } from '../firebase';
import { updateProfile } from 'firebase/auth';
import AvatarUpload from '../components/AvatarUpload';

import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const IVORY = '#FDFCF9';
const WARM_GRAY = '#888888';

export default function Profile() {
  const { currentUser } = useAuth();

  if (!currentUser) {
    return (
      <div style={pageWrap}>
        <Seo title="Profile • Blessed with a Kiss" description="Your private profile dashboard." noindex />
        <div style={card}>
          <h2 style={title}>Profile</h2>
          <p style={{ color: WARM_GRAY, marginTop: 6 }}>Please log in to view your profile.</p>
          <div style={{ marginTop: 12 }}>
            <Link to="/login" style={primaryBtn}>Go to Login</Link>
          </div>
        </div>
      </div>
    );
  }

  return <ProfileInner key={currentUser.uid} />;
}

function ProfileInner() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // --- Load memories for list + media library (LIVE)
  const [loading, setLoading] = React.useState(true);
  const [memories, setMemories] = React.useState([]);

  React.useEffect(() => {
    if (!currentUser?.uid) {
      setMemories([]);
      setLoading(false);
      return () => {};
    }

    setLoading(true);

    const qy = query(
      collection(db, 'memories'),
      where('userId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(
      qy,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          firestoreId: d.id,
          ...d.data(),
        }));
        setMemories(Array.isArray(list) ? list : []);
        setLoading(false);
      },
      (err) => {
        console.error('[Profile] memories snapshot failed:', err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [currentUser?.uid]);

  // --- Personal Info
  const [displayName, setDisplayName] = React.useState(currentUser.displayName || '');
  const [photoURL, setPhotoURL] = React.useState(currentUser.photoURL || '');
  const [savingInfo, setSavingInfo] = React.useState(false);
  const [infoMsg, setInfoMsg] = React.useState('');

  async function savePersonalInfo(e) {
    e?.preventDefault?.();
    setInfoMsg('');
    setSavingInfo(true);
    try {
      await updateProfile(auth.currentUser, {
        displayName: displayName.trim() || null,
        photoURL: photoURL || null,
      });
      await setUserProfile(currentUser.uid, {
        displayName: displayName.trim() || null,
        photoURL: photoURL || null,
      });
      setInfoMsg('Saved.');
    } catch (err) {
      console.error('[Profile] updateProfile failed:', err);
      setInfoMsg('Failed to save. See console for details.');
    } finally {
      setSavingInfo(false);
      setTimeout(() => setInfoMsg(''), 2500);
    }
  }

  const onAvatarUploaded = async (url) => {
    setPhotoURL(url);
    try {
      await updateProfile(auth.currentUser, { photoURL: url });
      await setUserProfile(currentUser.uid, { photoURL: url });
    } catch (e) {
      console.error('[Profile] Failed to update photoURL:', e);
    }
  };

  // --- Preferences (from Firestore users/{uid}.prefs) with live subscription
  const [isPublicProfile, setIsPublicProfile] = React.useState(false);
  const [defaultPublic, setDefaultPublic] = React.useState(false);
  const [defaultMediaPublic, setDefaultMediaPublic] = React.useState(false);
  const [emailSummaries, setEmailSummaries] = React.useState(true);
  const [savingPrefs, setSavingPrefs] = React.useState(false);
  const [prefsMsg, setPrefsMsg] = React.useState('');

  React.useEffect(() => {
    if (!currentUser?.uid) return;
    const unsub = watchUserPrefs(currentUser.uid, (prefs) => {
      setIsPublicProfile(!!prefs?.isPublicProfile);
      setDefaultPublic(!!prefs?.defaults?.isPublic);
      setDefaultMediaPublic(!!prefs?.defaults?.isMediaPublic);
      setEmailSummaries(
        typeof prefs?.notifications?.emailSummaries === 'boolean'
          ? prefs.notifications.emailSummaries
          : true
      );
    });
    return unsub;
  }, [currentUser?.uid]);

  async function savePrefs(e) {
    e?.preventDefault?.();
    setSavingPrefs(true);
    setPrefsMsg('');
    try {
      await saveUserPrefs(currentUser.uid, {
        isPublicProfile,
        defaults: { isPublic: defaultPublic, isMediaPublic: defaultMediaPublic },
        notifications: { emailSummaries },
      });
      setPrefsMsg('Saved.');
    } catch (err) {
      console.error('[Profile] savePrefs failed:', err);
      setPrefsMsg('Failed to save.');
    } finally {
      setSavingPrefs(false);
      setTimeout(() => setPrefsMsg(''), 2500);
    }
  }

  // --- Media library derived from memories
  const media = React.useMemo(() => {
    const urls = [];
    for (const m of memories) {
      if (Array.isArray(m.imageUrls)) {
        for (const u of m.imageUrls) {
          if (typeof u === 'string' && u.trim()) urls.push({ url: u, id: `${m.firestoreId || m.id}-${u}` });
        }
      } else if (m.imageUrl && typeof m.imageUrl === 'string') {
        urls.push({ url: m.imageUrl, id: `${m.firestoreId || m.id}-single` });
      }
    }
    return urls;
  }, [memories]);

  const createdAt = auth.currentUser?.metadata?.creationTime
    ? new Date(auth.currentUser.metadata.creationTime)
    : null;

  const pageUrl = typeof window !== 'undefined' ? `${window.location.origin}/profile` : undefined;

  return (
    <div style={pageWrap}>
      <Seo
        title="Profile • Blessed with a Kiss"
        description="Your private profile dashboard: personal info, settings, and your library."
        url={pageUrl}
        noindex
      />

      <h1 style={pageTitle}>Profile</h1>

      <div style={grid} className="bwak-profile-grid">
        {/* Personal Info */}
        <section style={card} aria-labelledby="pi-h">
          <h2 id="pi-h" style={title}>Personal Info</h2>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
            <AvatarUpload uid={currentUser.uid} currentURL={photoURL} onUploaded={onAvatarUploaded} />
            <div style={{ color: WARM_GRAY, fontSize: 13 }}>JPG/PNG, up to 5 MB.</div>
          </div>

          <form onSubmit={savePersonalInfo} style={{ display: 'grid', gap: 10 }}>
            <div>
              <label style={label}>Email</label>
              <input type="email" value={auth.currentUser?.email || ''} disabled style={inputDisabled} />
            </div>

            <div>
              <label style={label}>Display name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Add your name (optional)"
                style={input}
              />
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6 }}>
              <button type="submit" style={primaryBtn} disabled={savingInfo}>
                {savingInfo ? 'Saving…' : 'Save'}
              </button>
              {createdAt ? (
                <span style={{ color: WARM_GRAY, fontSize: 13 }}>
                  Joined {createdAt.toLocaleDateString()}
                </span>
              ) : null}
              {infoMsg ? <span style={{ color: WARM_GRAY, fontSize: 13 }}>{infoMsg}</span> : null}
            </div>
          </form>
        </section>

        {/* Balance / Wallet (placeholder) */}
        <section style={card} aria-labelledby="bal-h">
          <h2 id="bal-h" style={title}>Balance (Wallet)</h2>
          <div style={{ color: WARM_GRAY, marginBottom: 8 }}>
            Coming soon. You’ll see your balance and purchase history here.
          </div>
          <div style={balanceBox}>
            <div style={{ fontSize: 12, color: WARM_GRAY }}>Current balance</div>
            <div style={{ fontFamily: 'serif', fontSize: 28, fontWeight: 800 }}>$0.00</div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" style={ghostBtn} disabled>Deposit</button>
            <button type="button" style={ghostBtn} disabled>Withdraw</button>
          </div>
        </section>

        {/* Profile Settings */}
        <section style={card} aria-labelledby="ps-h">
          <h2 id="ps-h" style={title}>Profile Settings</h2>
          <form onSubmit={savePrefs} style={{ display: 'grid', gap: 10 }}>
            <label style={styles.check}>
              <input
                type="checkbox"
                checked={isPublicProfile}
                onChange={(e) => setIsPublicProfile(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              Public profile (basic info only)
            </label>

            <label style={styles.check}>
              <input
                type="checkbox"
                checked={emailSummaries}
                onChange={(e) => setEmailSummaries(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              Email me occasional summaries
            </label>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6 }}>
              <button type="submit" style={primaryBtn} disabled={savingPrefs}>
                {savingPrefs ? 'Saving…' : 'Save Settings'}
              </button>
              {prefsMsg ? <span style={{ color: WARM_GRAY, fontSize: 13 }}>{prefsMsg}</span> : null}
            </div>
          </form>
        </section>

        {/* Memory Defaults */}
        <section style={card} aria-labelledby="md-h">
          <h2 id="md-h" style={title}>Memory Defaults</h2>
          <p style={{ color: WARM_GRAY, marginTop: 0, marginBottom: 10 }}>
            These are applied to new memories by default (you can still change them per memory).
          </p>
          <form onSubmit={savePrefs} style={{ display: 'grid', gap: 10 }}>
            <label style={styles.check}>
              <input
                type="checkbox"
                checked={defaultPublic}
                onChange={(e) => setDefaultPublic(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              New memories are <strong>Public</strong>
            </label>

            <label style={styles.check}>
              <input
                type="checkbox"
                checked={defaultMediaPublic}
                onChange={(e) => setDefaultMediaPublic(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              Allow media to be public by default (viewable by logged-in users)
            </label>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6 }}>
              <button type="submit" style={primaryBtn} disabled={savingPrefs}>
                {savingPrefs ? 'Saving…' : 'Save Defaults'}
              </button>
              {prefsMsg ? <span style={{ color: WARM_GRAY, fontSize: 13 }}>{prefsMsg}</span> : null}
            </div>
          </form>
        </section>

        {/* Memories List */}
        <section style={card} aria-labelledby="ml-h">
          <h2 id="ml-h" style={title}>Your Memories</h2>
          {loading ? (
            <div role="status" aria-label="Loading your memories" style={{ display: 'grid', gap: 8 }}>
              <SkeletonLine />
              <SkeletonLine />
              <SkeletonLine />
            </div>
          ) : memories.length === 0 ? (
            <div style={{ color: WARM_GRAY }}>
              You haven’t created any memories yet.
              <button
                type="button"
                style={{ ...linkBtn, marginLeft: 8 }}
                onClick={() => {
                  const el = document.getElementById('memory-form');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  else navigate('/');
                }}
              >
                Start a Memory
              </button>
            </div>
          ) : (
            <ul style={listReset}>
              {memories.map((m) => {
                const ttl = m.title || m.date || 'Untitled';
                const id = m.firestoreId || m.id;

                const minted = isMemoryMarkedMinted(m);

                return (
                  <li key={id} style={row}>
                    <div style={{ minWidth: 80, color: WARM_GRAY, fontSize: 12 }}>
                      {m.date || '—'}
                    </div>
                    <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{ttl}</span>
                      {minted ? (
                        <span style={{ fontSize: 12, fontWeight: 900, color: '#0F766E' }}>
                          ✅ Minted
                        </span>
                      ) : null}
                    </div>
                    <div style={{ marginLeft: 'auto' }}>
                      <Link to={`/m/${id}`} style={tinyBtn}>Open ↗</Link>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Danger Zone */}
        <section style={card} aria-labelledby="dz-h">
          <h2 id="dz-h" style={{ ...title, color: '#B42318' }}>Danger Zone</h2>
          <p style={{ color: WARM_GRAY, marginTop: 0 }}>
            These actions are sensitive. They’ll be enabled after we complete backend flows.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" style={{ ...dangerBtn, opacity: 0.6 }} disabled>Export my data</button>
            <button type="button" style={{ ...dangerBtn, opacity: 0.6 }} disabled>Delete my account</button>
          </div>
        </section>
      </div>
    </div>
  );
}

/* --- skeletons --- */
function SkeletonBlock({ height = 140 }) {
  return (
    <div
      style={{
        height,
        borderRadius: 12,
        background:
          'linear-gradient(90deg, rgba(253,252,249,0.06) 25%, rgba(253,252,249,0.13) 37%, rgba(253,252,249,0.06) 63%)',
        backgroundSize: '400% 100%',
        animation: 'bwak-shimmer 1.2s ease-in-out infinite',
        border: '1px solid rgba(201,164,76,0.20)',
      }}
    />
  );
}
function SkeletonLine() { return <SkeletonBlock height={20} />; }

/* --- styles --- */
const pageWrap = { minHeight: 'calc(100dvh - 64px)', background: NAVY, padding: '18px 12px 28px', color: IVORY };
const pageTitle = { margin: '8px auto 12px', maxWidth: 1100, fontFamily: 'serif', fontSize: 28 };
const grid = { maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr', gap: 12 };
const card = { background: IVORY, color: NAVY, borderRadius: 16, border: '1px solid rgba(201,164,76,0.35)', boxShadow: '0 8px 28px rgba(10,26,51,0.25)', padding: 16 };
const title = { margin: '0 0 8px', fontFamily: 'serif', fontSize: 20 };
const label = { display: 'block', fontWeight: 800, marginBottom: 6 };
const input = { width: '100%', padding: 12, borderRadius: 12, border: '1px solid rgba(10,26,51,0.15)', background: '#fff', color: NAVY, outline: 'none', fontSize: 14 };
const inputDisabled = { ...input, opacity: 0.7 };
const primaryBtn = { display: 'inline-block', background: GOLD, color: NAVY, border: 'none', borderRadius: 12, padding: '10px 14px', fontWeight: 900, cursor: 'pointer', textDecoration: 'none' };

const ghostBtn = {
  background: 'transparent',
  color: GOLD,
  border: `1px solid ${GOLD}`,
  borderRadius: 12,
  padding: '10px 14px',
  fontWeight: 800,
  cursor: 'pointer',
};

const linkBtn = { ...ghostBtn, padding: '6px 10px', fontSize: 13 };
const balanceBox = { background: '#fff', border: '1px solid rgba(10,26,51,0.12)', borderRadius: 12, padding: 12 };
const listReset = { listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 };
const row = { display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid rgba(10,26,51,0.12)', borderRadius: 12, padding: 10 };
const tinyBtn = { ...ghostBtn, padding: '6px 10px', fontSize: 12, textDecoration: 'none' };
const dangerBtn = {
  background: '#B42318',
  color: IVORY,
  border: 'none',
  borderRadius: 12,
  padding: '10px 14px',
  fontWeight: 900,
  cursor: 'not-allowed',
};

// checkbox row style
const styles = {
  check: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    color: NAVY,
    background: '#fff',
    borderRadius: 10,
    padding: '8px 10px',
    border: '1px solid rgba(10,26,51,0.12)',
  },
};

/* keyframes */
(() => {
  if (typeof document === 'undefined') return;
  const exists = document.getElementById('bwak-shimmer-keyframes');
  if (exists) return;
  const el = document.createElement('style');
  el.id = 'bwak-shimmer-keyframes';
  el.textContent = `
    @keyframes bwak-shimmer {
      0% { background-position: 0% 0; }
      100% { background-position: 100% 0; }
    }
    @media (min-width: 900px) {
      body .bwak-profile-grid { grid-template-columns: 1fr 1fr; }
    }
  `;
  document.head.appendChild(el);
})();
// ==========================
// End Version 5 — pages/Profile.js
// ==========================
